import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { updatePanelTitles } from '../../../utils/updatePanelTitles';
import {
  selectSimpleSupply,
  selectSimpleSupplyInstance,
  selectComponents,
  selectFeeders,
  selectLastCalculationTime,
  recalculateAllElectrical,
  recalculateUpstreamChain
} from '../../../redux/simpleSupplySlice';

/**
 * Simple SupplyPoint Event Hook - Redux state değişikliklerini izler
 * Change detection, visual updates ve automatic recalculation yapar
 * CRUD işlemleri için direkt Redux actions kullanın (addLoadToComponent, vs.)
 */
export const useSupplyPoint = (scene = null) => {
  const dispatch = useDispatch();
  const simpleSupply = useSelector(selectSimpleSupply);
  const simpleSupplyInstance = useSelector(selectSimpleSupplyInstance);
  const components = useSelector(selectComponents);
  const feeders = useSelector(selectFeeders);
  const lastCalculationTime = useSelector(selectLastCalculationTime);

  const previousRef = useRef(simpleSupply);

  // Redux actions (sadece internal kullanım için)
  const actions = {
    recalculateAllElectrical,
    recalculateUpstreamChain
  };

  /**
   * Change detection ve event handling
   */
  useEffect(() => {
    const prev = previousRef.current;

    if (prev !== simpleSupply) {
      const componentsChanged = !_.isEqual(simpleSupply.components, prev.components);
      const feedersChanged = !_.isEqual(simpleSupply.feeders, prev.feeders);

      if (componentsChanged) {
        handleComponentsChange(simpleSupply, prev);
      }

      if (feedersChanged) {
       

        handleFeedersChange(simpleSupply, prev);
      }

      previousRef.current = simpleSupply;
    }
  }, [simpleSupply]);

  /**
   * Component değişiklikleri handler
   */
  const handleComponentsChange = useCallback((current, previous) => {
    // Yeni component eklendi mi?
    if (current.components.length > previous.components.length) {
      const newComponents = current.components.filter(c =>
        !previous.components.find(p => p.id === c.id)
      );

      newComponents.forEach(newComponent => {
 
        // Eğer load/branch component'i ise, kendisinden başlayarak upstream'i yeniden hesapla
        if (newComponent.kind === 'load' || newComponent.kind === 'branch') {
           // Yeni component'ten başlayarak upstream chain'i hesapla (kendisi ve parent'lar dahil)
          dispatch(actions.recalculateUpstreamChain(newComponent.id));
        }
      });
    }

    // Component silindi mi?
    if (current.components.length < previous.components.length) {
       dispatch(actions.recalculateAllElectrical());
    }

    // Component güncellendi mi?
    current.components.forEach(currentComponent => {
      const prevComponent = previous.components.find(p => p.id === currentComponent.id);

      if (prevComponent && !_.isEqual(currentComponent.electrical, prevComponent.electrical)) {
 
        // Panel ise title'ını güncelle
        if ((currentComponent.kind === 'dist_panel' || currentComponent.kind === 'meter_panel') && scene) {
          updatePanelTitles(scene, current);
        }

        // Upstream chain'i yeniden hesapla
        dispatch(actions.recalculateUpstreamChain(currentComponent.id));
      }
    });
  }, [dispatch, actions, scene]);

  /**
   * Feeder değişiklikleri handler
   */
  const handleFeedersChange = useCallback((current, previous) => {
    // Yeni feeder eklendi mi?
    if (current.feeders.length > previous.feeders.length) {
      const newFeeders = current.feeders.filter(f =>
        !previous.feeders.find(p => p.id === f.id)
      );

      newFeeders.forEach(newFeeder => {
 
        // Feeder'ın source component'ini yeniden hesapla
        dispatch(actions.recalculateUpstreamChain(newFeeder.from));
      });
    }

    // Feeder güncellendi mi?
    current.feeders.forEach(currentFeeder => {
      const prevFeeder = previous.feeders.find(p => p.id === currentFeeder.id);

      if (prevFeeder) {
        const cableChanged = !_.isEqual(currentFeeder.cable, prevFeeder.cable);
        const powerChanged = !_.isEqual(currentFeeder.power, prevFeeder.power);

        if (cableChanged || powerChanged) {
          // Kablo değişikliği durumunda capacity recalculation gerekebilir
        }
      }
    });
  }, [dispatch, actions]);

  /**
   * Utility methods (read-only operations)
   */

  const validateSystem = useCallback(() => { 
    // Basic validation - component count, feeder connections
    const componentCount = components.length;
    const feederCount = feeders.length;


    return componentCount > 0 ? [] : ['No components found'];
  }, [components, feeders]);

  const getSummary = useCallback(() => {
    return {
      componentCount: components.length,
      feederCount: feeders.length,
      lastCalculationTime,
      totalComponents: {
        supplies: components.filter(c => c.kind === 'supply').length,
        panels: components.filter(c => c.kind === 'dist_panel' || c.kind === 'meter_panel').length,
        loads: components.filter(c => c.kind === 'load' || c.kind === 'branch').length
      }
    };
  }, [components, feeders, lastCalculationTime]);

  return {
    // State (read-only)
    simpleSupply,
    simpleSupplyInstance,
    components,
    feeders,
    lastCalculationTime,

    // Utilities (read-only)
    validateSystem,
    getSummary,

    // Direct access to store actions (advanced usage only)
    actions,
    dispatch
  };
};

/**
 * Simple event listener hook - belirli event'leri dinlemek için
 */
export const useSupplyPointEventListener = (eventTypes, callback) => {
  const simpleSupply = useSelector(selectSimpleSupply);
  const previousRef = useRef(simpleSupply);

  useEffect(() => {
    const prev = previousRef.current;

    if (prev !== simpleSupply) {
      eventTypes.forEach(eventType => {
        let hasChanged = false;
        let eventData = {};

        switch (eventType) {
          case 'COMPONENTS_CHANGED':
            hasChanged = !_.isEqual(simpleSupply.components, prev.components);
            eventData = {
              current: simpleSupply.components,
              previous: prev.components,
              added: simpleSupply.components.filter(c =>
                !prev.components.find(p => p.id === c.id)
              ),
              removed: prev.components.filter(p =>
                !simpleSupply.components.find(c => c.id === p.id)
              )
            };
            break;

          case 'FEEDERS_CHANGED':
            hasChanged = !_.isEqual(simpleSupply.feeders, prev.feeders);
            eventData = {
              current: simpleSupply.feeders,
              previous: prev.feeders,
              added: simpleSupply.feeders.filter(f =>
                !prev.feeders.find(p => p.id === f.id)
              ),
              removed: prev.feeders.filter(p =>
                !simpleSupply.feeders.find(f => f.id === p.id)
              )
            };
            break;

          case 'ELECTRICAL_CALCULATED':
            hasChanged = simpleSupply.lastCalculationTime !== prev.lastCalculationTime;
            eventData = {
              calculationTime: simpleSupply.lastCalculationTime,
              calculationCount: simpleSupply.calculationCount
            };
            break;

          default:
            break;
        }

        if (hasChanged) {
          callback({
            type: eventType,
            ...eventData
          });
        }
      });

      previousRef.current = simpleSupply;
    }
  }, [simpleSupply, eventTypes, callback]);
};

/**
 * Component selector hook
 */
export const useSupplyPointComponent = (componentId) => {
  const component = useSelector(state =>
    state.simpleSupply.components.find(c => c.id === componentId)
  );

  const outgoingFeeders = useSelector(state =>
    state.simpleSupply.feeders.filter(f => f.from === componentId)
  );

  const incomingFeeders = useSelector(state =>
    state.simpleSupply.feeders.filter(f => f.to === componentId)
  );

  return {
    component,
    outgoingFeeders,
    incomingFeeders,
    isConnected: incomingFeeders.length > 0 || outgoingFeeders.length > 0
  };
};

/**
 * Feeder selector hook
 */
export const useSupplyPointFeeder = (feederId) => {
  const feeder = useSelector(state =>
    state.simpleSupply.feeders.find(f => f.id === feederId)
  );

  const fromComponent = useSelector(state =>
    feeder ? state.simpleSupply.components.find(c => c.id === feeder.from) : null
  );

  const toComponent = useSelector(state =>
    feeder ? state.simpleSupply.components.find(c => c.id === feeder.to) : null
  );

  return {
    feeder,
    fromComponent,
    toComponent,
    isValid: !!(feeder && fromComponent && toComponent)
  };
};

/**
 * Event constants
 */
export const SIMPLE_SUPPLY_POINT_EVENTS = {
  COMPONENTS_CHANGED: 'COMPONENTS_CHANGED',
  FEEDERS_CHANGED: 'FEEDERS_CHANGED',
  ELECTRICAL_CALCULATED: 'ELECTRICAL_CALCULATED'
};